import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  Clock,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Mail,
  RefreshCw,
  User,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { createDgiPortalUser } from "@/services/authService";
import { cn } from "@/lib/utils";
import {
  acceptTrabajo,
  getIngresoSimplificado,
  startIngresoSimplificado,
  submitConfirmationCode,
  submitPostulacion,
  type IngresoSimplificadoEntity,
  type IngresoSimplificadoStatus,
} from "@/services/ingresoSimplificadoService";

// Same operador notification inbox the standard accountant flow registers
// against the DGI e-Factura user — keep in sync with the constant in
// accountant-client-onboarding-page.tsx.
const DGI_NOTIFICATIONS_EMAIL = "notificaciones-dgi@fiscalo.uy";

// Statuses where an automation / DGI-side transition is in flight, so we
// poll to flip the UI without a manual refresh. User-action and terminal
// states don't poll.
const LIVE_STATUSES: ReadonlySet<IngresoSimplificadoStatus> = new Set([
  "postulacion_running",
  "aceptacion_running",
  "trabajo_accepted",
  "authorized",
  "recibido_sent",
  "awaiting_produccion_role",
]);

// Macro-step index (0-based) for the linear stepper. Multiple fine-grained
// statuses collapse into one visible phase.
function macroStep(status: IngresoSimplificadoStatus): number {
  switch (status) {
    case "pending":
    case "portal_user_ready":
      return 0; // Usuario e-Factura
    case "postulacion_running":
    case "awaiting_confirmation_code":
    case "postulacion_submitted":
    case "awaiting_proveedor_acceptance":
    case "aceptacion_running":
    case "trabajo_accepted":
      return 1; // Postulación
    case "authorized":
    case "recibido_sent":
      return 2; // Autorización DGI
    case "awaiting_produccion_role":
    case "completed":
      return 3; // Producción
    default:
      return 0; // failed / rejected → keep on first phase visually
  }
}

const MACRO_TOTAL = 4;

export function AccountantHomologacionSimplificadaFlow({
  displayName,
  onExit,
  onContinue,
  portalUserRowExists,
}: {
  displayName: string;
  onExit: () => void;
  /** Advance past the "Homologación completa" panel to the remaining standard
   *  onboarding steps (certificate, CAE, sucursal, plan…). Only surfaced once
   *  the ingreso flow reaches `completed`; the parent flips to the standard
   *  wizard. Without it the accountant dead-ends on the completed panel. */
  onContinue: () => void;
  /** Whether a CompanyDgiPortalUser row already exists for this tenant
   *  (tenant.hasDgiPortalUser). Lets the operador-creation sub-step skip
   *  straight to "Comenzar" after a page refresh, since the row — and thus
   *  the encrypted password the /start automation needs — is already there. */
  portalUserRowExists: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [acting, setActing] = useState(false);
  const [code, setCode] = useState("");

  const {
    data: entity,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["ingreso-simplificado"] as const,
    queryFn: getIngresoSimplificado,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status && LIVE_STATUSES.has(status) ? 5_000 : false;
    },
  });

  const status: IngresoSimplificadoStatus = entity?.status ?? "pending";

  // Run a state-machine action, surface a toast, then refetch so the panel
  // advances. Mirrors the plain async-handler style of the sibling
  // accountant onboarding page (no useMutation ceremony per action).
  const run = async (
    fn: () => Promise<unknown>,
    successMsg: string,
  ): Promise<void> => {
    if (acting) return;
    setActing(true);
    try {
      await fn();
      await refetch();
      toast.success(successMsg);
    } catch {
      toast.error(
        t(
          "homologacionSimplificada.actionError",
          "No pudimos completar la acción. Probá de nuevo.",
        ),
      );
    } finally {
      setActing(false);
    }
  };

  const handleStart = (): void =>
    void run(
      startIngresoSimplificado,
      t(
        "homologacionSimplificada.startedToast",
        "Usuario de homologación creado. Registralo en DGI.",
      ),
    );

  const handlePostulacion = (): void =>
    void run(
      submitPostulacion,
      t(
        "homologacionSimplificada.postulacionStartedToast",
        "Postulación iniciada en DGI…",
      ),
    );

  // Retry from a `failed`/`rejected` state — re-run ONLY the phase that failed,
  // never the whole flow. Discriminate by which session id has been stamped:
  //  - aceptacionSessionId set → Flow B (SALKANTAI accepts the trabajo) failed;
  //    retry just that (acceptTrabajo). Does NOT redo the postulación.
  //  - postulacionSessionId set → the postulación (Flow A) failed; retry it
  //    (submitPostulacion). Does NOT recreate the e-Factura user.
  //  - neither → the user-creation step failed; retry that (startIngreso…).
  const handleRetry = (): void => {
    if (entity?.aceptacionSessionId) {
      handleAcceptTrabajo();
    } else if (entity?.postulacionSessionId) {
      handlePostulacion();
    } else {
      handleStart();
    }
  };

  const handleSubmitCode = (): void => {
    const trimmed = code.trim();
    if (trimmed === "") {
      toast.error(
        t(
          "homologacionSimplificada.code.empty",
          "Ingresá el código de confirmación.",
        ),
      );
      return;
    }
    void run(
      () => submitConfirmationCode(trimmed),
      t(
        "homologacionSimplificada.code.submittedToast",
        "Código enviado. Confirmando la postulación…",
      ),
    ).then(() => setCode(""));
  };

  const handleAcceptTrabajo = (): void =>
    void run(
      acceptTrabajo,
      t(
        "homologacionSimplificada.acceptedToast",
        "Fiscalo confirmado como proveedor del cliente.",
      ),
    );

  const handleCopyEmail = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(DGI_NOTIFICATIONS_EMAIL);
      toast.success(
        t("homologacionSimplificada.emailCopied", "Email copiado."),
      );
    } catch {
      toast.error(
        t(
          "homologacionSimplificada.emailCopyError",
          "No pudimos copiar el email.",
        ),
      );
    }
  };

  const currentMacro = macroStep(status);
  const isTerminalFail = status === "failed" || status === "rejected";

  const macroLabels = [
    t("homologacionSimplificada.step.portalUser", "Usuario"),
    t("homologacionSimplificada.step.postulacion", "Postulación"),
    t("homologacionSimplificada.step.authorization", "Autorización"),
    t("homologacionSimplificada.step.produccion", "Producción"),
  ];

  return (
    <div className="fx-acct-setup">
      <div className="fx-acct-setup-meta">
        <span className="fx-acct-setup-eyebrow">{displayName}</span>
        <span className="fx-acct-setup-progress">
          {t(
            "homologacionSimplificada.progress",
            "Homologación simplificada",
          )}
          {" · "}
          {t(
            "homologacionSimplificada.progressStep",
            "Paso {{current}} de {{total}}",
            { current: Math.min(currentMacro + 1, MACRO_TOTAL), total: MACRO_TOTAL },
          )}
        </span>
      </div>

      <div className="fx-acct-phase-stepper" role="tablist">
        {macroLabels.map((label, idx) => {
          const done = status === "completed" ? true : idx < currentMacro;
          const isCurrent = idx === currentMacro && status !== "completed";
          return (
            <div
              key={label}
              role="tab"
              aria-selected={isCurrent}
              className={cn(
                "fx-acct-phase-step",
                isCurrent && "is-current",
                done && "is-done",
              )}
            >
              <span className="fx-acct-phase-step-dot">
                {done ? <Check aria-hidden /> : <span>{idx + 1}</span>}
              </span>
              <span className="fx-acct-phase-step-label">{label}</span>
            </div>
          );
        })}
      </div>

      <h1 className="fx-onb-h1">
        {t("homologacionSimplificada.titleLead", "Homologación")}{" "}
        <span className="ital">
          {t("homologacionSimplificada.titleAccent", "simplificada.")}
        </span>
      </h1>
      <p className="fx-onb-lead">
        {t(
          "homologacionSimplificada.lead",
          "Damos de alta al cliente como emisor electrónico en DGI usando la homologación simplificada. Fiscalo automatiza cada paso; vos solo registrás el usuario y el código cuando haga falta.",
        )}
      </p>

      {isLoading ? (
        <div className="fx-acct-section">
          <div className="fx-acct-section-body">
            <Loader2 className="animate-spin" aria-hidden />
          </div>
        </div>
      ) : isTerminalFail ? (
        <FailedPanel
          entity={entity ?? null}
          acting={acting}
          onRetry={handleRetry}
        />
      ) : (
        <StatusPanel
          status={status}
          entity={entity ?? null}
          acting={acting}
          code={code}
          setCode={setCode}
          accountantDocumentNumber={user?.documentNumber ?? null}
          portalUserRowExists={portalUserRowExists}
          onStart={handleStart}
          onPostulacion={handlePostulacion}
          onSubmitCode={handleSubmitCode}
          onAcceptTrabajo={handleAcceptTrabajo}
          onCopyEmail={handleCopyEmail}
        />
      )}

      <div className="fx-onb-actions">
        <div className="side">
          <button
            type="button"
            className="fx-onb-btn fx-onb-btn-secondary"
            onClick={onExit}
          >
            <ArrowLeft aria-hidden />
            {t("homologacionSimplificada.backToClients", "Volver a clientes")}
          </button>
        </div>
        {status === "completed" ? (
          <div className="side end">
            <button
              type="button"
              className="fx-onb-btn fx-onb-btn-accent"
              onClick={onContinue}
            >
              {t(
                "homologacionSimplificada.completed.continue",
                "Continuar con la configuración",
              )}
              <ArrowRight aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SectionShell({
  icon,
  title,
  meta,
  done = false,
  children,
}: {
  icon: JSX.Element;
  title: string;
  meta?: string;
  done?: boolean;
  children?: React.ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className={cn("fx-acct-section", done && "is-done")}>
      <div className="fx-acct-section-head">
        <div className="fx-acct-section-ico">
          {done ? <Check aria-hidden /> : icon}
        </div>
        <div className="fx-acct-section-text">
          <div className="title">{title}</div>
          {meta ? <div className="meta">{meta}</div> : null}
        </div>
        {done ? (
          <span className="fx-acct-section-status">
            {t("homologacionSimplificada.status.done", "Listo")}
          </span>
        ) : null}
      </div>
      {children ? (
        <div className="fx-acct-section-body">{children}</div>
      ) : null}
    </div>
  );
}

function Spinner(): JSX.Element {
  return <Loader2 className="animate-spin" aria-hidden />;
}

/**
 * Collapsible that fetches and shows the stored e-Factura user credentials on
 * demand — needed for the Producción step (log into DGI as the client's
 * e-Factura user to add the Producción role). Idempotent read: the backend
 * returns the same stored password and never rotates it without an explicit
 * regenerate.
 */
function StoredEfacturaCredentials(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(true);
  const [creds, setCreds] = useState<{
    password: string;
    documentNumber: string;
  } | null>(null);

  const toggle = async (): Promise<void> => {
    const next = !open;
    setOpen(next);
    if (!next || creds || loading) return;
    setLoading(true);
    try {
      const r = await createDgiPortalUser({});
      setCreds({ password: r.password, documentNumber: r.documentNumber });
      setRevealed(true);
    } catch {
      setOpen(false);
      toast.error(
        t(
          "homologacionSimplificada.produccion.credsError",
          "No pudimos recuperar las credenciales del usuario e-Factura.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = async (): Promise<void> => {
    if (!creds) return;
    try {
      await navigator.clipboard.writeText(creds.password);
      toast.success(
        t("homologacionSimplificada.operador.passwordCopied", "Contraseña copiada."),
      );
    } catch {
      toast.error(
        t(
          "homologacionSimplificada.operador.passwordCopyError",
          "No pudimos copiar la contraseña.",
        ),
      );
    }
  };

  const copyEmail = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(DGI_NOTIFICATIONS_EMAIL);
      toast.success(
        t("homologacionSimplificada.operador.emailCopied", "Email copiado."),
      );
    } catch {
      toast.error(
        t(
          "homologacionSimplificada.operador.emailCopyError",
          "No pudimos copiar el email.",
        ),
      );
    }
  };

  return (
    <div className="fx-acct-creds-disclosure">
      <button
        type="button"
        className="fx-acct-section-btn fx-acct-section-btn-secondary"
        onClick={() => void toggle()}
        disabled={loading}
      >
        {loading ? <Spinner /> : <KeyRound aria-hidden />}
        {open
          ? t(
              "homologacionSimplificada.produccion.hideCreds",
              "Ocultar credenciales del usuario e-Factura",
            )
          : t(
              "homologacionSimplificada.produccion.showCreds",
              "Ver credenciales del usuario e-Factura",
            )}
      </button>

      {open && creds ? (
        <div className="fx-acct-creds-card">
          <div className="fx-acct-creds-row">
            <div className="fx-acct-creds-main">
              <div className="fx-acct-creds-label">
                {t(
                  "homologacionSimplificada.operador.documentLabel",
                  "Cédula del titular",
                )}
              </div>
              <div className="fx-acct-creds-value">{creds.documentNumber}</div>
            </div>
          </div>
          <div className="fx-acct-creds-row">
            <div className="fx-acct-creds-main">
              <div className="fx-acct-creds-label">
                {t(
                  "homologacionSimplificada.operador.passwordLabel",
                  "Contraseña generada",
                )}
              </div>
              <div className="fx-acct-creds-value">
                {revealed ? creds.password : "••••••••••"}
              </div>
            </div>
            <div className="fx-acct-creds-actions">
              <button
                type="button"
                className="fx-acct-creds-icon-btn"
                onClick={() => setRevealed((r) => !r)}
                aria-label={
                  revealed
                    ? t("homologacionSimplificada.operador.hide", "Ocultar")
                    : t("homologacionSimplificada.operador.reveal", "Mostrar")
                }
                title={
                  revealed
                    ? t("homologacionSimplificada.operador.hide", "Ocultar")
                    : t("homologacionSimplificada.operador.reveal", "Mostrar")
                }
              >
                {revealed ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
              </button>
              <button
                type="button"
                className="fx-acct-creds-icon-btn"
                onClick={() => void copyPassword()}
              >
                <Copy aria-hidden />
                {t("homologacionSimplificada.operador.copy", "Copiar")}
              </button>
            </div>
          </div>
          {/* The e-Factura user was created against this inbox, so the
              accountant needs it to log in / recover the account — same row
              the creation step shows, kept identical here. */}
          <div className="fx-acct-creds-row">
            <div className="fx-acct-creds-main">
              <div className="fx-acct-creds-label">
                {t(
                  "homologacionSimplificada.operador.emailLabel",
                  "Email de notificaciones",
                )}
              </div>
              <div className="fx-acct-creds-value">
                {DGI_NOTIFICATIONS_EMAIL}
              </div>
            </div>
            <div className="fx-acct-creds-actions">
              <button
                type="button"
                className="fx-acct-creds-icon-btn"
                onClick={() => void copyEmail()}
              >
                <Copy aria-hidden />
                {t("homologacionSimplificada.operador.copy", "Copiar")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StatusPanel({
  status,
  entity,
  acting,
  code,
  setCode,
  accountantDocumentNumber,
  portalUserRowExists,
  onStart,
  onPostulacion,
  onSubmitCode,
  onAcceptTrabajo,
  onCopyEmail,
}: {
  status: IngresoSimplificadoStatus;
  entity: IngresoSimplificadoEntity | null;
  acting: boolean;
  code: string;
  setCode: (v: string) => void;
  accountantDocumentNumber: string | null;
  portalUserRowExists: boolean;
  onStart: () => void;
  onPostulacion: () => void;
  onSubmitCode: () => void;
  onAcceptTrabajo: () => void;
  onCopyEmail: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  // The confirmation code is read from the DGI email and injected
  // automatically (ingreso-postulacion-code-processor). Manual entry is only a
  // fallback for when the auto-read doesn't land — hidden by default.
  const [showManualCode, setShowManualCode] = useState(false);

  switch (status) {
    case "pending":
      return (
        <OperadorCreationPanel
          acting={acting}
          portalUserRowExists={portalUserRowExists}
          accountantDocumentNumber={accountantDocumentNumber}
          onCopyEmail={onCopyEmail}
          onStart={onStart}
        />
      );

    case "portal_user_ready":
      return (
        <SectionShell
          icon={<User aria-hidden />}
          title={t(
            "homologacionSimplificada.portalUser.title",
            "Registrá el usuario e-Factura en DGI",
          )}
          meta={t(
            "homologacionSimplificada.portalUser.meta",
            "Creá el usuario de e-Factura del cliente en el portal de DGI con estos datos y solo el rol de homologación simplificada. Después iniciá la postulación.",
          )}
        >
          <div className="fx-acct-creds-card">
            <div className="fx-acct-creds-row">
              <div className="fx-acct-creds-main">
                <div className="fx-acct-creds-label">
                  {t(
                    "homologacionSimplificada.portalUser.cedulaLabel",
                    "Cédula (tuya, como contador)",
                  )}
                </div>
                <div className="fx-acct-creds-value">
                  {accountantDocumentNumber ??
                    t(
                      "homologacionSimplificada.portalUser.cedulaMissing",
                      "Completá tu cédula en tu perfil",
                    )}
                </div>
              </div>
            </div>
            <div className="fx-acct-creds-row">
              <div className="fx-acct-creds-main">
                <div className="fx-acct-creds-label">
                  {t(
                    "homologacionSimplificada.portalUser.emailLabel",
                    "Email de notificaciones",
                  )}
                </div>
                <div className="fx-acct-creds-value">
                  {DGI_NOTIFICATIONS_EMAIL}
                </div>
              </div>
              <div className="fx-acct-creds-actions">
                <button
                  type="button"
                  className="fx-acct-creds-icon-btn"
                  onClick={onCopyEmail}
                >
                  <Copy aria-hidden />
                  {t("homologacionSimplificada.operador.copy", "Copiar")}
                </button>
              </div>
            </div>
          </div>
          <div className="fx-onb-roles">
            <div className="role">
              <span className="check" aria-hidden>
                <Check />
              </span>
              <strong>
                {t(
                  "homologacionSimplificada.portalUser.role",
                  "Homologación simplificada",
                )}
              </strong>
            </div>
            <span className="note">
              {t(
                "homologacionSimplificada.portalUser.rolesNote",
                "Marcá solo este rol por ahora. El rol de Producción se agrega al final.",
              )}
            </span>
          </div>
          <div className="fx-acct-section-cta">
            <button
              type="button"
              className="fx-acct-section-btn"
              onClick={onPostulacion}
              disabled={acting}
            >
              {acting ? <Spinner /> : null}
              {t("homologacionSimplificada.portalUser.startPostulacion", "Iniciar postulación")}
              <ArrowRight aria-hidden />
            </button>
          </div>
        </SectionShell>
      );

    case "postulacion_running":
      return (
        <RunningPanel
          title={t(
            "homologacionSimplificada.postulacionRunning.title",
            "Completando la postulación en DGI…",
          )}
          body={t(
            "homologacionSimplificada.postulacionRunning.body",
            "Estamos enviando la postulación en el portal de DGI. Esto puede tardar unos minutos — te avisamos cuando llegue el código de confirmación.",
          )}
        />
      );

    case "awaiting_confirmation_code":
      return (
        <SectionShell
          icon={<Mail aria-hidden />}
          title={t(
            "homologacionSimplificada.code.title",
            "Código de confirmación de postulación",
          )}
          meta={t(
            "homologacionSimplificada.code.metaAuto",
            "DGI envía un código por correo al email de notificaciones. Fiscalo lo lee e ingresa automáticamente — este paso se completa solo.",
          )}
        >
          <div className="fx-acct-fiscalo-running">
            <Loader2
              className="fx-acct-fiscalo-running-spin animate-spin"
              aria-hidden
            />
            <div className="fx-acct-fiscalo-running-text">
              <div className="fx-acct-fiscalo-running-title">
                {t(
                  "homologacionSimplificada.code.waitingTitle",
                  "Esperando el código de DGI…",
                )}
              </div>
              <div className="fx-acct-fiscalo-running-body">
                {t(
                  "homologacionSimplificada.code.waitingBody",
                  "Lo leemos del correo automáticamente. Podés dejar esta pantalla abierta.",
                )}
              </div>
            </div>
          </div>

          {/* Fallback: manual entry only if the auto-read doesn't land. */}
          {showManualCode ? (
            <>
              <label className="fx-acct-form-field">
                <span className="fx-acct-form-label">
                  {t(
                    "homologacionSimplificada.code.label",
                    "Código de confirmación",
                  )}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  className="fx-acct-form-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
              </label>
              <div className="fx-acct-section-cta">
                <button
                  type="button"
                  className="fx-acct-section-btn"
                  onClick={onSubmitCode}
                  disabled={acting || code.trim() === ""}
                >
                  {acting ? <Spinner /> : null}
                  {t("homologacionSimplificada.code.submit", "Confirmar código")}
                  <ArrowRight aria-hidden />
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="fx-acct-section-btn fx-acct-section-btn-ghost"
              onClick={() => setShowManualCode(true)}
            >
              {t(
                "homologacionSimplificada.code.manualFallback",
                "El código no se cargó solo — ingresarlo manualmente",
              )}
            </button>
          )}
        </SectionShell>
      );

    case "postulacion_submitted":
    case "awaiting_proveedor_acceptance":
      return (
        <SectionShell
          icon={<BadgeCheck aria-hidden />}
          title={t(
            "homologacionSimplificada.acceptTrabajo.title",
            "Fiscalo confirma como tu proveedor",
          )}
          meta={t(
            "homologacionSimplificada.acceptTrabajo.meta",
            "La postulación quedó enviada. Ahora Fiscalo confirma ante DGI que será el proveedor de facturación electrónica del cliente. Se hace automáticamente; si no avanza en unos minutos, podés dispararlo manualmente.",
          )}
        >
          <div className="fx-acct-section-cta">
            <button
              type="button"
              className="fx-acct-section-btn"
              onClick={onAcceptTrabajo}
              disabled={acting}
            >
              {acting ? <Spinner /> : null}
              {t(
                "homologacionSimplificada.acceptTrabajo.accept",
                "Confirmar ahora",
              )}
              <ArrowRight aria-hidden />
            </button>
          </div>
        </SectionShell>
      );

    case "aceptacion_running":
      return (
        <RunningPanel
          title={t(
            "homologacionSimplificada.aceptacionRunning.title",
            "Activando a Fiscalo como proveedor…",
          )}
          body={t(
            "homologacionSimplificada.aceptacionRunning.body",
            "Fiscalo está confirmando ante DGI que será el proveedor de facturación electrónica del cliente. En breve DGI habilita la autorización.",
          )}
        />
      );

    case "trabajo_accepted":
    case "authorized":
    case "recibido_sent":
      return (
        <>
          <SectionShell
            icon={<Clock aria-hidden />}
            title={t(
              "homologacionSimplificada.awaitingAuth.title",
              "Esperando autorización de DGI",
            )}
            meta={t(
              "homologacionSimplificada.awaitingAuth.meta",
              "Fiscalo ya confirmó como proveedor. Ahora esperamos que DGI autorice al cliente como emisor electrónico y confirme la recepción. Este paso se completa automáticamente.",
            )}
          />
        </>
      );

    case "awaiting_produccion_role":
      return (
        <>
          <SectionShell
            icon={<KeyRound aria-hidden />}
            title={t(
              "homologacionSimplificada.produccion.title",
              "Agregá el rol de PRODUCCIÓN en DGI",
            )}
            meta={t(
              "homologacionSimplificada.produccion.meta",
              "Último paso: agregá el rol de Producción al usuario de e-Factura en el portal de DGI.",
            )}
          >
            <ol className="fx-onb-dgi-steps">
              <li>
                {t(
                  "homologacionSimplificada.produccion.s1",
                  "Entrá al portal de DGI con el usuario de e-Factura del cliente.",
                )}
              </li>
              <li>
                {t(
                  "homologacionSimplificada.produccion.s2",
                  "Abrí la administración del usuario y editá sus roles.",
                )}
              </li>
              <li>
                {t(
                  "homologacionSimplificada.produccion.s3",
                  "Marcá el rol de Producción y guardá los cambios.",
                )}
              </li>
            </ol>
            <StoredEfacturaCredentials />
            <p className="fx-acct-auto-note" role="status">
              {t(
                "homologacionSimplificada.produccion.autoNote",
                "Cuando DGI confirme el rol de producción por correo, este paso se completa automáticamente. Te avisamos apenas se confirme.",
              )}
            </p>
          </SectionShell>
        </>
      );

    case "completed":
      return (
        <SectionShell
          icon={<BadgeCheck aria-hidden />}
          title={t(
            "homologacionSimplificada.completed.title",
            "Homologación completa",
          )}
          meta={t(
            "homologacionSimplificada.completed.meta",
            "El emisor está autorizado en producción. Ya puede facturar electrónicamente con Fiscalo.",
          )}
          done
        />
      );

    default:
      return (
        <SectionShell
          icon={<Clock aria-hidden />}
          title={t("homologacionSimplificada.unknown.title", "En proceso")}
          meta={t(
            "homologacionSimplificada.unknown.meta",
            "Estamos procesando este paso.",
          )}
        />
      );
  }
}

/**
 * First panel of the scratch flow (no-row / `pending` state). The backend
 * `/start` automation decrypts an existing CompanyDgiPortalUser password to
 * register the e-Factura user in DGI with the homologación role — so the row
 * must exist first. This sub-step collects the titular's cédula, creates that
 * row via the idempotent createDgiPortalUser service, shows the operador
 * credentials, and only THEN enables "Comenzar homologación simplificada".
 */
function OperadorCreationPanel({
  acting,
  portalUserRowExists,
  accountantDocumentNumber,
  onCopyEmail,
  onStart,
}: {
  acting: boolean;
  portalUserRowExists: boolean;
  accountantDocumentNumber: string | null;
  onCopyEmail: () => void;
  onStart: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  // No titular/legal-rep cédula is stored on the tenant, so this is a
  // manual-only field. Default it to the accountant's own document as a
  // convenience starting point (editable) — the acting user often is the
  // titular for freshly-created client companies.
  const [cedula, setCedula] = useState(accountantDocumentNumber ?? "");
  const [generating, setGenerating] = useState(false);
  const [creds, setCreds] = useState<{
    password: string;
    documentNumber: string;
  } | null>(null);
  // Password shown by default (copy-first). The reveal toggle only lets the
  // accountant hide it for shoulder-surfing, not the other way around.
  const [revealed, setRevealed] = useState(true);

  const canStart = creds != null || portalUserRowExists;

  const generate = async (opts?: { silent?: boolean }): Promise<void> => {
    if (generating) return;
    const doc = cedula.trim();
    // A cédula is only required for first-time creation. When a row already
    // exists the backend ignores the override and returns the stored creds.
    if (!portalUserRowExists && doc === "") {
      if (!opts?.silent)
        toast.error(
          t(
            "homologacionSimplificada.operador.cedulaRequired",
            "Ingresá la cédula del titular.",
          ),
        );
      return;
    }
    setGenerating(true);
    try {
      const result = await createDgiPortalUser(
        doc === ""
          ? {}
          : { documentType: "CI", documentNumber: doc },
      );
      setCreds({
        password: result.password,
        documentNumber: result.documentNumber,
      });
      setRevealed(true);
      // Silent on the auto-fetch of already-stored creds (page load / resume) —
      // nothing was "created", so a success toast would be misleading.
      if (!opts?.silent)
        toast.success(
          t(
            "homologacionSimplificada.operador.generatedToast",
            "Usuario e-Factura creado. Copiá la contraseña antes de continuar.",
          ),
        );
    } catch {
      if (!opts?.silent)
        toast.error(
          t(
            "homologacionSimplificada.operador.generateError",
            "No pudimos crear el usuario e-Factura. Probá de nuevo.",
          ),
        );
    } finally {
      setGenerating(false);
    }
  };

  // Show the credentials at once: when the operador row already exists
  // (returning to this step / page refresh), auto-fetch the stored password
  // on mount so the accountant sees the cédula + password immediately instead
  // of having to click "Ver credenciales". Idempotent read — the backend
  // returns the same stored password and never rotates it without an explicit
  // regenerate. Runs once.
  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (autoFetchedRef.current) return;
    if (portalUserRowExists && creds == null && !generating) {
      autoFetchedRef.current = true;
      void generate({ silent: true });
    }
    // Intentionally mount-only: portalUserRowExists is stable for the panel's
    // lifetime and we guard re-entry with the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalUserRowExists]);

  const copyPassword = async (): Promise<void> => {
    if (!creds) return;
    try {
      await navigator.clipboard.writeText(creds.password);
      toast.success(
        t("homologacionSimplificada.operador.passwordCopied", "Contraseña copiada."),
      );
    } catch {
      toast.error(
        t(
          "homologacionSimplificada.operador.passwordCopyError",
          "No pudimos copiar la contraseña.",
        ),
      );
    }
  };

  return (
    <SectionShell
      icon={<User aria-hidden />}
      title={t(
        "homologacionSimplificada.operador.title",
        "Creá el usuario e-Factura",
      )}
      meta={t(
        "homologacionSimplificada.operador.meta",
        "Generamos el usuario de e-Factura del cliente en DGI con el rol de homologación simplificada. Ingresá la cédula del titular y generá las credenciales antes de comenzar.",
      )}
    >
      {creds == null && !portalUserRowExists ? (
        <label className="fx-acct-form-field">
          <span className="fx-acct-form-label">
            {t(
              "homologacionSimplificada.operador.cedulaLabel",
              "Cédula del titular",
            )}
          </span>
          <input
            type="text"
            inputMode="numeric"
            className="fx-acct-form-input"
            value={cedula}
            onChange={(e) => setCedula(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="00000000"
            maxLength={9}
            disabled={generating}
          />
        </label>
      ) : null}

      {/* Unified credentials card: cédula + contraseña + email as aligned
          label/value rows with inline copy. Password is shown by default
          (copy-first) with a quiet reveal toggle. */}
      <div className="fx-acct-creds-card">
        {creds != null ? (
          <>
            <div className="fx-acct-creds-row">
              <div className="fx-acct-creds-main">
                <div className="fx-acct-creds-label">
                  {t(
                    "homologacionSimplificada.operador.documentLabel",
                    "Cédula del titular",
                  )}
                </div>
                <div className="fx-acct-creds-value">
                  {creds.documentNumber}
                </div>
              </div>
            </div>
            <div className="fx-acct-creds-row">
              <div className="fx-acct-creds-main">
                <div className="fx-acct-creds-label">
                  {t(
                    "homologacionSimplificada.operador.passwordLabel",
                    "Contraseña generada",
                  )}
                </div>
                <div className="fx-acct-creds-value">
                  {revealed ? creds.password : "••••••••••"}
                </div>
              </div>
              <div className="fx-acct-creds-actions">
                <button
                  type="button"
                  className="fx-acct-creds-icon-btn"
                  onClick={() => setRevealed((r) => !r)}
                  aria-label={
                    revealed
                      ? t("homologacionSimplificada.operador.hide", "Ocultar")
                      : t("homologacionSimplificada.operador.reveal", "Mostrar")
                  }
                  title={
                    revealed
                      ? t("homologacionSimplificada.operador.hide", "Ocultar")
                      : t("homologacionSimplificada.operador.reveal", "Mostrar")
                  }
                >
                  {revealed ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
                </button>
                <button
                  type="button"
                  className="fx-acct-creds-icon-btn"
                  onClick={() => void copyPassword()}
                >
                  <Copy aria-hidden />
                  {t("homologacionSimplificada.operador.copy", "Copiar")}
                </button>
              </div>
            </div>
          </>
        ) : null}

        <div className="fx-acct-creds-row">
          <div className="fx-acct-creds-main">
            <div className="fx-acct-creds-label">
              {t(
                "homologacionSimplificada.operador.emailLabel",
                "Email de notificaciones",
              )}
            </div>
            <div className="fx-acct-creds-value">{DGI_NOTIFICATIONS_EMAIL}</div>
          </div>
          <div className="fx-acct-creds-actions">
            <button
              type="button"
              className="fx-acct-creds-icon-btn"
              onClick={onCopyEmail}
            >
              <Copy aria-hidden />
              {t("homologacionSimplificada.operador.copy", "Copiar")}
            </button>
          </div>
        </div>
      </div>

      <div className="fx-onb-roles">
        <div className="role">
          <span className="check" aria-hidden>
            <Check />
          </span>
          <strong>
            {t(
              "homologacionSimplificada.operador.role",
              "Homologación simplificada",
            )}
          </strong>
        </div>
        <span className="note">
          {t(
            "homologacionSimplificada.operador.rolesNote",
            "El usuario se crea solo con este rol. El de Producción se agrega al final, cuando el emisor pasa a producción.",
          )}
        </span>
      </div>

      <div className="fx-acct-section-cta">
        <button
          type="button"
          className={cn(
            "fx-acct-section-btn",
            // Once credentials exist, "Regenerar" is a rare escape hatch —
            // keep it quiet so "Comenzar" reads as the primary action. Before
            // that, "Generar" / "Ver credenciales" is the primary step
            // (Comenzar is disabled), so it stays a solid secondary button.
            creds != null
              ? "fx-acct-section-btn-ghost"
              : "fx-acct-section-btn-secondary",
          )}
          onClick={() => void generate()}
          disabled={generating || acting}
        >
          {generating ? <Spinner /> : <RefreshCw aria-hidden />}
          {creds != null
            ? t(
                "homologacionSimplificada.operador.regenerate",
                "Regenerar",
              )
            : portalUserRowExists
              ? t(
                  "homologacionSimplificada.operador.reveal",
                  "Ver credenciales",
                )
              : t(
                  "homologacionSimplificada.operador.generate",
                  "Generar usuario e-Factura",
                )}
        </button>
        <button
          type="button"
          className="fx-acct-section-btn"
          onClick={onStart}
          disabled={!canStart || acting || generating}
          title={
            !canStart
              ? t(
                  "homologacionSimplificada.operador.startBlockedHint",
                  "Generá el usuario e-Factura antes de comenzar.",
                )
              : undefined
          }
        >
          {acting ? <Spinner /> : null}
          {t(
            "homologacionSimplificada.intro.start",
            "Comenzar homologación simplificada",
          )}
          <ArrowRight aria-hidden />
        </button>
      </div>
    </SectionShell>
  );
}

function RunningPanel({
  title,
  body,
}: {
  title: string;
  body: string;
}): JSX.Element {
  return (
    <div className="fx-acct-section">
      <div className="fx-acct-section-body fx-acct-fiscalo-running">
        <Loader2
          className="fx-acct-fiscalo-running-spin animate-spin"
          aria-hidden
        />
        <div className="fx-acct-fiscalo-running-text">
          <div className="fx-acct-fiscalo-running-title">{title}</div>
          <div className="fx-acct-fiscalo-running-body">{body}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Translate a raw automation failure reason (English + technical, e.g.
 * "Aceptar trabajo modal (#gxp0_ifrm) did not render") into a plain-Spanish
 * explanation for the accountant. The raw string stays on the entity for
 * logs/debugging; only the display is humanised. Unknown reasons fall back to
 * a generic message.
 */
function friendlyIngresoFailureReason(
  raw: string | null | undefined,
  t: (key: string, defaultValue: string) => string,
): string {
  const generic = t(
    "homologacionSimplificada.failed.reason.generic",
    "Hubo un problema al procesar la homologación en DGI. Reintentá; si sigue fallando, escribinos.",
  );
  if (!raw) return generic;
  const r = raw.toLowerCase();
  if (r.includes("did not render")) {
    return t(
      "homologacionSimplificada.failed.reason.modal",
      "El portal de DGI no llegó a abrir una ventana necesaria para continuar. Suele ser algo temporal — reintentá en unos minutos.",
    );
  }
  if (r.includes("not found among") || r.includes("ruc mismatch")) {
    return t(
      "homologacionSimplificada.failed.reason.proveedor",
      "No pudimos seleccionar a Fiscalo como proveedor en el portal de DGI. Reintentá en unos minutos; si continúa, escribinos.",
    );
  }
  if (r.includes("confirmation code")) {
    return t(
      "homologacionSimplificada.failed.reason.code",
      "No recibimos a tiempo el código de confirmación que DGI envía por correo. Reintentá la postulación.",
    );
  }
  if (r.includes("login")) {
    return t(
      "homologacionSimplificada.failed.reason.login",
      "No pudimos iniciar sesión en el portal de DGI con el usuario e-Factura del cliente. Verificá las credenciales y reintentá.",
    );
  }
  if (
    r.includes("not found") ||
    r.includes("not reached") ||
    r.includes("missing") ||
    r.includes("not present")
  ) {
    return t(
      "homologacionSimplificada.failed.reason.step",
      "Un paso del portal de DGI no respondió como esperábamos. Suele ser temporal — reintentá en unos minutos.",
    );
  }
  return generic;
}

function FailedPanel({
  entity,
  acting,
  onRetry,
}: {
  entity: IngresoSimplificadoEntity | null;
  acting: boolean;
  onRetry: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const rejected = entity?.status === "rejected";
  return (
    <SectionShell
      icon={<AlertTriangle aria-hidden />}
      title={
        rejected
          ? t("homologacionSimplificada.failed.rejectedTitle", "Postulación rechazada")
          : t("homologacionSimplificada.failed.title", "La homologación falló")
      }
      meta={friendlyIngresoFailureReason(entity?.failureReason, t)}
    >
      <div className="fx-acct-section-cta">
        <button
          type="button"
          className="fx-acct-section-btn"
          onClick={onRetry}
          disabled={acting}
        >
          {acting ? <Spinner /> : <RefreshCw aria-hidden />}
          {t("homologacionSimplificada.failed.retry", "Reintentar")}
        </button>
      </div>
    </SectionShell>
  );
}
